import type { FilterSpecification, LayerSpecification, StyleSpecification } from 'maplibre-gl'
import type { ProximiioStyleResponse } from '@/types/proximi'
import { apiUrl } from '@/lib/config'

/** Everything on a Proximi.io style that carries level-tagged geometry. */
export const MAIN_SOURCE_ID = 'main'

const PROXIMIIO_HOSTS = /^https?:\/\/api\.proximi\.(fi|io)\//i

/**
 * Paths on the Proximi.io host that are served **without** a token.
 *
 * The default style's basemap is OpenMapTiles vector tiles (`/data/v3.json` and
 * the `/data/v3/{z}/{x}/{y}.pbf` URLs listed inside it) with glyphs from
 * `/fonts/…`; all of them answer 200 to an anonymous request. Sending them
 * through the proxy would buy nothing — there is no credential to keep off the
 * client — and would cost real work: MapLibre resolves the TileJSON itself, so
 * the tile URLs *inside* that document would have to be rewritten server-side
 * too. Left direct, they are fetched straight from Proximi.io and cached by the
 * browser like any other CDN asset.
 */
const PUBLIC_PREFIXES = ['fonts/', 'data/']

/**
 * Rewrites a Proximi.io URL onto our own read-only proxy.
 *
 * The style's `glyphs`, `sprite` and raster tile URLs all point back at
 * `api.proximi.fi` and need the platform bearer token. MapTap solves this in the
 * browser with a MapLibre `transformRequest`; here the request goes through the
 * Rust API instead, so the platform token never reaches the client at all.
 *
 * MapLibre cannot set a header on these requests, which is why they used to
 * carry `?token=`. They no longer carry anything: the URL is same-origin, so the
 * `Path=/v1` session cookie rides on it by itself — and nothing secret ends up
 * in an access log on the way.
 */
export function proxyUrl(url: string | undefined): string | undefined {
  if (!url || !PROXIMIIO_HOSTS.test(url)) return url
  const path = url.replace(PROXIMIIO_HOSTS, '')
  if (PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix))) return url
  return apiUrl(`/v1/proximi/${path}`)
}

/**
 * Turns the platform's style document into a style MapLibre will accept.
 *
 * The API returns the style without a `version`, with its asset URLs pointing at
 * the platform, and with `main` declared but empty — the features are fetched
 * separately from `/v7/geo/features`.
 */
export function toMapLibreStyle(
  response: ProximiioStyleResponse,
  features: GeoJSON.FeatureCollection,
): StyleSpecification {
  const sources: NonNullable<StyleSpecification['sources']> = {}

  for (const [id, source] of Object.entries(response.sources ?? {})) {
    const next = { ...(source as Record<string, unknown>) }
    if (typeof next.url === 'string') next.url = proxyUrl(next.url)
    if (Array.isArray(next.tiles)) {
      next.tiles = (next.tiles as string[]).map((tile) => proxyUrl(tile) ?? tile)
    }
    sources[id] = next as never
  }

  // `main` is where every level-filtered layer reads from. The platform ships it
  // as an empty placeholder, so fill it here — otherwise the map renders blank
  // with no error, which is the single most confusing failure mode of this stack.
  sources[MAIN_SOURCE_ID] = { type: 'geojson', data: features, tolerance: 0.1 } as never

  // Sources the live layers own. Kept separate from `main` so a features
  // refresh never clobbers the dots, and vice versa.
  sources['liveview-users'] = {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  } as never
  sources['liveview-trails'] = {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  } as never
  sources['liveview-accuracy'] = {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  } as never
  // Dashed rings around positions LiveView is playing itself. A circle layer
  // cannot dash, so the ring is real geometry and needs its own source.
  sources['liveview-journey-rings'] = {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  } as never
  for (const id of [
    'liveview-capture-points',
    'liveview-capture-cells',
    'liveview-capture-route',
    // The journey being looked at, drawn read-only. Its own sources so picking
    // a journey never disturbs the dots, or the venue.
    'liveview-journey-line',
    'liveview-journey-points',
  ]) {
    sources[id] = { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as never
  }

  const layers = (response.layers ?? []).filter(
    (layer) => !!layer && typeof layer === 'object',
  ) as LayerSpecification[]

  const style: Record<string, unknown> = {
    version: 8,
    name: response.name ?? 'Proximi.io',
    metadata: response.metadata,
    center: response.center,
    zoom: response.zoom,
    bearing: response.bearing,
    pitch: response.pitch,
    sources,
    layers,
  }

  // MapLibre validates these as strings and rejects an explicitly `undefined`
  // key, so a style without glyphs or a sprite must omit them entirely.
  const glyphs = proxyUrl(response.glyphs)
  if (glyphs) style.glyphs = glyphs
  const sprite = proxyUrl(response.sprite)
  if (sprite) style.sprite = sprite

  return style as StyleSpecification
}

type Expression = unknown[]

const isExpression = (value: unknown): value is Expression => Array.isArray(value)

/** `["get", "level"]` or `["to-number", ["get", "level"]]`. */
function readsLevel(expression: unknown): boolean {
  if (!isExpression(expression)) return false
  const [op, arg] = expression
  if (op === 'get' && arg === 'level') return true
  if (op === 'to-number') return readsLevel(arg)
  return false
}

/**
 * Rewrites every `level` comparison inside a filter to the selected floor.
 *
 * Proximi.io styles encode floor visibility as literal comparisons against the
 * level number — `["==", ["to-number", ["get", "level"]], 0]` — plus, on the
 * level-changer layer, a `__level_N` string key. Port of MapTap's
 * `BaseLayer.setFilterLevel` (`src/layers/base_layer.ts`).
 */
export function retargetFilter(
  filter: FilterSpecification | undefined,
  level: number,
  layerId?: string,
): FilterSpecification | undefined {
  if (!filter) return filter

  const walk = (node: unknown): unknown => {
    if (!isExpression(node)) return node
    const [op] = node

    if ((op === '==' || op === '<=' || op === '>=' || op === '<' || op === '>') && readsLevel(node[1])) {
      const next = [...node]
      next[2] = level
      return next
    }

    // Level changers point at a neighbouring floor by string key.
    if (layerId === 'proximiio-levelchangers' && typeof node[1] === 'string' && node[1].startsWith('__level_')) {
      const next = [...node]
      next[1] = `__level_${level}`
      return next
    }

    return node.map(walk)
  }

  return walk(filter) as FilterSpecification
}

/** Layer ids whose filters mention `level`, computed once per style. */
export function levelAwareLayers(style: ProximiioStyleResponse): Array<{
  id: string
  filter: FilterSpecification
}> {
  const found: Array<{ id: string; filter: FilterSpecification }> = []
  for (const layer of style.layers ?? []) {
    const filter = (layer as { filter?: FilterSpecification }).filter
    if (!filter) continue
    if (JSON.stringify(filter).includes('"level"')) {
      found.push({ id: layer.id, filter })
    }
  }
  return found
}

/** How many features sit on each `level`. */
export function featureLevelCounts(features: GeoJSON.FeatureCollection): Map<number, number> {
  const counts = new Map<number, number>()
  for (const feature of features.features) {
    const raw = feature.properties?.level
    const level = typeof raw === 'string' ? Number(raw) : raw
    if (typeof level === 'number' && Number.isFinite(level)) {
      counts.set(level, (counts.get(level) ?? 0) + 1)
    }
  }
  return counts
}

/** Distinct `level` values present in the loaded features, ascending. */
export function levelsFromFeatures(features: GeoJSON.FeatureCollection): number[] {
  return [...featureLevelCounts(features).keys()].sort((a, b) => a - b)
}
